import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumbs from "../components/Breadcrumbs";
import WorkoutProgressTable from "../components/WorkoutProgressTable";
import ExerciseProgressTable from "../components/ExerciseProgressTable";
import WorkoutNoteModal from "../components/WorkoutNoteModal";
import EmptyStateMessage from "../components/EmptyStateMessage";
import SeoHead from "../components/SeoHead";
import ExerciseFlowSteps from "../components/ExerciseFlowSteps";
import ClearPlannedModal from "../components/ClearPlannedModal";
import "./Home.css";
import "../styles/Common.css";
import { useHomeData } from "../hooks/useHomeData";
import { formatWeightRange } from "../utils/formatWeightRange";
import { todayYMDLocal } from "../utils/dateUtils";
import { useI18n } from "../i18n/i18n";
import { getLocalizedExerciseName } from "../utils/exerciseName";
import { getAllExercisesCatalog } from "../services/customExerciseService";
import { buildLocalizedAlternates, getPrerenderPublicPaths } from "../seo/routePolicy";
import { muscleGroups } from "../data/muscleGroups";
import { useSnapshotRenderMode } from "../utils/prerender";
import { buildProgressDates } from "./homeDateWindow";
import { calculateExerciseVolume, calculateExerciseCardioMinutes, formatVolumeLoad, formatDualVolume } from "../utils/volumeLoad";
import { getExerciseMetricType } from "../services/exerciseService";

function Home() {
    const { t, locale, localizePath } = useI18n();
    const { dates, loggedExercises, exerciseLog, workoutNotes, updateWorkoutNote, clearPlannedWorkout } = useHomeData();
    const [noteModal, setNoteModal] = useState({
        isOpen: false,
        date: "",
        initialNote: "",
    });
    const [isClearModalOpen, setIsClearModalOpen] = useState(false);

    const handleOpenNoteModal = ({ date, note }) => {
        setNoteModal({
            isOpen: true,
            date,
            initialNote: note !== undefined ? note : (workoutNotes?.[date] || ""),
        });
    };

    const handleCloseNoteModal = () => {
        setNoteModal((prev) => ({ ...prev, isOpen: false }));
    };

    const handleSaveNote = async (date, noteText) => {
        if (updateWorkoutNote) {
            await updateWorkoutNote(date, noteText);
        }
        handleCloseNoteModal();
    };
    const snapshotMode = useSnapshotRenderMode();
    const [displayToday, setDisplayToday] = useState(() => {
        if (typeof document !== "undefined") {
            const saved = document.getElementById("home-snapshot-today")?.getAttribute("data-today");
            if (saved) {
                return saved;
            }
        }

        return todayYMDLocal();
    });
    const [progressView, setProgressView] = useState("muscleGroups");
    const [progressDateIndex, setProgressDateIndex] = useState(0);
    const [showIndirect, setShowIndirect] = useState(true);

    useEffect(() => {
        setDisplayToday(todayYMDLocal());
    }, []);

    useEffect(() => {
        try {
            const saved = localStorage.getItem("home_showIndirect");
            if (saved !== null) {
                setShowIndirect(saved === "true");
            }
        } catch (_) {
            // ignore storage errors
        }
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem("home_showIndirect", String(showIndirect));
        } catch (_) {
            // ignore storage errors
        }
    }, [showIndirect]);

    const exerciseMap = useMemo(
        () => Object.fromEntries(getAllExercisesCatalog().map((exercise) => [exercise.id, exercise])),
        []
    );

    const hasAnyLoggedExercises = useMemo(() => {
        return Object.values(exerciseLog || {}).some((day) => day && Object.keys(day).length > 0);
    }, [exerciseLog]);

    const isSetPlanned = (s) => Boolean(s && s.type === "planned");
    const hasPlannedExercises = useMemo(() => {
        return (loggedExercises || []).some(({ sets }) =>
            Array.isArray(sets) && sets.some(isSetPlanned)
        );
    }, [loggedExercises]);

    const plannedExercisesCount = useMemo(() => {
        return (loggedExercises || []).filter(({ sets }) =>
            Array.isArray(sets) && sets.some(isSetPlanned)
        ).length;
    }, [loggedExercises]);

    const handleOpenClearModal = () => {
        setIsClearModalOpen(true);
    };

    const handleCloseClearModal = () => {
        setIsClearModalOpen(false);
    };

    const handleConfirmClearPlanned = async () => {
        if (clearPlannedWorkout) {
            await clearPlannedWorkout(displayToday);
        }
        setIsClearModalOpen(false);
    };

    const todayVolumeLoad = useMemo(() => {
        return (loggedExercises || []).reduce(
            (sum, item) => sum + calculateExerciseVolume(item.sets),
            0
        );
    }, [loggedExercises]);

    const todayCardioMinutes = useMemo(() => {
        return (loggedExercises || []).reduce(
            (sum, item) => sum + calculateExerciseCardioMinutes(item.sets),
            0
        );
    }, [loggedExercises]);

    const todayWorkoutNote = workoutNotes?.[displayToday] || "";
    const hasTodayWorkoutNote = Boolean(todayWorkoutNote && todayWorkoutNote.trim().length > 0);
    const visibleDates = useMemo(() => {
        return buildProgressDates({
            dates,
            displayToday,
            exerciseLog,
            workoutNotes,
            minimumDates: 5,
            maxDates: 60,
        });
    }, [dates, displayToday, exerciseLog, workoutNotes]);

    const todayIndex = useMemo(() => {
        return visibleDates.findIndex((d) => d === displayToday);
    }, [visibleDates, displayToday]);

    const isTodayActive = todayIndex !== -1 ? progressDateIndex === todayIndex : progressDateIndex === 0;

    const handleGoToToday = () => {
        setProgressDateIndex(todayIndex !== -1 ? todayIndex : 0);
    };

    const handlePrevDay = () => {
        setProgressDateIndex((prev) => prev + 1);
    };

    const handleNextDay = () => {
        setProgressDateIndex((prev) => Math.max(0, prev - 1));
    };
    const homeJsonLd = useMemo(
        () => {
            const siteUrl = "https://fit-log.com";
            const dashboardUrl = `${siteUrl}${localizePath("/")}`;

            const breadcrumbList = {
                "@type": "BreadcrumbList",
                "itemListElement": [
                    {
                        "@type": "ListItem",
                        "position": 1,
                        "name": t("breadcrumbs.dashboard"),
                        "item": dashboardUrl
                    }
                ]
            };

            const appSchema = {
                "@type": "SoftwareApplication",
                "name": "Fit-Log",
                "applicationCategory": "HealthApplication",
                "operatingSystem": "Web",
                "description": t("home.jsonLdDescription"),
                "url": dashboardUrl,
            };

            return {
                "@context": "https://schema.org",
                "@graph": [breadcrumbList, appSchema]
            };
        },
        [t, localizePath]
    );
    const prerenderPaths = useMemo(
        () => getPrerenderPublicPaths(muscleGroups.map((group) => group.id)),
        []
    );

    return (
        <div className="homePage pageContainer">
            <SeoHead
                title={`Fit-Log - ${t("home.title")}`}
                description={t("home.jsonLdDescription")}
                canonicalPath={localizePath("/")}
                jsonLd={homeJsonLd}
                alternateLocales={buildLocalizedAlternates("/")}
            />
            <Breadcrumbs current={t("breadcrumbs.dashboard")} />
            <header className="homeHero">
                <h1 className="sectionHeading">{t("home.title")}</h1>
                <p className="homeSubtitle">{t("home.subtitle")}</p>
            </header>
            <span id="home-snapshot-today" data-today={displayToday} hidden aria-hidden="true" />
            {snapshotMode && (
                <nav aria-hidden="true" style={{ display: "none" }}>
                    {prerenderPaths.map((path) => (
                        <a key={path} href={path}>{path}</a>
                    ))}
                </nav>
            )}

            <div className="section">
                <div className="sectionHeadingRow">
                    <h2 className="sectionHeading">{t("home.loggedToday")}</h2>
                    <ExerciseFlowSteps activeStep={0} />
                    <div className="sectionHeadingActions">
                        {hasPlannedExercises && (
                            <button
                                type="button"
                                className="clearPlanButton"
                                onClick={handleOpenClearModal}
                                aria-label={t("home.clearPlannedExercises")}
                                title={t("home.clearPlannedExercises")}
                            >
                                <span aria-hidden="true" className="clearPlanIcon">✕</span>
                            </button>
                        )}
                        <Link
                            to={localizePath("/plan-workout")}
                            className="planWorkoutButton"
                            aria-label={t("home.planWorkout")}
                            title={t("home.planWorkout")}
                        >
                            <span aria-hidden="true">🪄</span>
                            <span className="planWorkoutButtonText">{t("home.planWorkoutShort")}</span>
                        </Link>
                        <Link
                            to={localizePath("/SelectExercise")}
                            className="addExerciseButton addExerciseButton--muscleGroup"
                            aria-label={t("home.addExercise")}
                            title={t("home.addExercise")}
                        >
                            <span aria-hidden="true">+</span>
                        </Link>
                    </div>
                </div>
                {loggedExercises.length === 0 ? (
                    <EmptyStateMessage />
                ) : (
                    <table className="compactExerciseTable">
                        <colgroup>
                            <col className="colExercise" />
                            <col className="colSets" />
                            <col className="colWeight" />
                        </colgroup>
                        <thead>
                        <tr className="volumeLoadRow todayVolumeLoadRow">
                            <th colSpan={3} className="todayVolumeLoadCell">
                                <span className="todayVolumeLoadLabel">{t("home.todaysVolumeLoad")}:</span>{" "}
                                <span className="todayVolumeLoadValue">
                                    {formatDualVolume(todayVolumeLoad, todayCardioMinutes, locale, {
                                        emptyPlaceholder: "0",
                                        cardioUnit: t("home.cardioUnit"),
                                    })}
                                </span>
                            </th>
                        </tr>
                        <tr>
                            <th>{t("home.exercise")}</th>
                            <th>{t("home.setsCount")}</th>
                            <th>{t("home.weight")}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {loggedExercises.map(({ exerciseId, sets }) => {
                            const exercise = exerciseMap[exerciseId];
                            const exerciseName = exercise
                                ? getLocalizedExerciseName(exercise, t)
                                : t("home.unknownExercise", { id: exerciseId });

                            const safeSets = Array.isArray(sets) ? sets : [];
                            const isSetDone = (s) => !s || s.type !== "planned";
                            const isSetPlanned = (s) => Boolean(s && s.type === "planned");
                            const allDone = safeSets.length > 0 && safeSets.every(isSetDone);
                            const hasDone = safeSets.some(isSetDone);
                            const hasPlanned = safeSets.some(isSetPlanned);
                            const partiallyDone = hasDone && hasPlanned;

                            const isCardio = getExerciseMetricType(exerciseId) === "cardio";
                            const cardioMinutes = calculateExerciseCardioMinutes(safeSets);

                            const statusMark = allDone ? (
                                <span
                                    className="exerciseStatusMark exerciseStatusMark--done"
                                    title={t("home.statusAllDone")}
                                    aria-label={t("home.statusAllDone")}
                                >
                                    ✓
                                </span>
                            ) : partiallyDone ? (
                                <span
                                    className="exerciseStatusMark exerciseStatusMark--partial"
                                    title={t("home.statusPartiallyDone")}
                                    aria-label={t("home.statusPartiallyDone")}
                                >
                                    •
                                </span>
                            ) : null;

                            return (
                                <tr key={exerciseId}>
                                    <td>
                                        {snapshotMode ? (
                                            <span className="linkButton appLinkButton">
                                                {statusMark}
                                                <span className="exerciseLinkText">{exerciseName}</span>
                                            </span>
                                        ) : (
                                            <Link
                                                to={localizePath(`/LogExercise/${exerciseId}/${displayToday}`)}
                                                className="linkButton appLinkButton"
                                            >
                                                {statusMark}
                                                <span className="exerciseLinkText">{exerciseName}</span>
                                            </Link>
                                        )}
                                    </td>
                                    <td>{sets.length}</td>
                                    <td>
                                        {isCardio
                                            ? (cardioMinutes > 0 ? `${cardioMinutes} ${t("home.cardioUnit")}` : t("common.na"))
                                            : formatWeightRange(sets, { naLabel: t("common.na") })}
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                )}

                <div className="todayNoteCard" data-testid="today-workout-note">
                    <div className="todayNoteHeader">
                        <div className="todayNoteTitleBlock">
                            <span className="todayNoteIcon" aria-hidden="true">📝</span>
                            <span className="todayNoteLabel">{t("home.todayWorkoutNote")}</span>
                        </div>
                        <button
                            type="button"
                            className={`todayNoteActionButton ${hasTodayWorkoutNote ? "todayNoteActionButton--hasNote" : "todayNoteActionButton--empty"}`}
                            onClick={() => handleOpenNoteModal({ date: displayToday, note: todayWorkoutNote })}
                            aria-label={hasTodayWorkoutNote ? t("home.editNote") : t("home.addNote")}
                        >
                            {hasTodayWorkoutNote ? (
                                <>
                                    <span className="todayNoteActionIcon" aria-hidden="true">✏️</span>
                                    <span>{t("home.editNote")}</span>
                                </>
                            ) : (
                                <>
                                    <span className="todayNoteActionPlus" aria-hidden="true">+</span>
                                    <span>{t("home.addNote")}</span>
                                </>
                            )}
                        </button>
                    </div>
                    {hasTodayWorkoutNote ? (
                        <div
                            className="todayNoteBody todayNoteBody--filled"
                            onClick={() => handleOpenNoteModal({ date: displayToday, note: todayWorkoutNote })}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleOpenNoteModal({ date: displayToday, note: todayWorkoutNote });
                                }
                            }}
                            role="button"
                            tabIndex={0}
                            title={t("home.editNote")}
                        >
                            <p className="todayNoteText">{todayWorkoutNote}</p>
                        </div>
                    ) : (
                        <div
                            className="todayNoteBody todayNoteBody--empty"
                            onClick={() => handleOpenNoteModal({ date: displayToday, note: todayWorkoutNote })}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleOpenNoteModal({ date: displayToday, note: todayWorkoutNote });
                                }
                            }}
                            role="button"
                            tabIndex={0}
                            title={t("home.addNote")}
                        >
                            <span className="todayNotePlaceholderText">{t("home.clickToAddNote")}</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="section">
                <div className="sectionHeadingRow">
                    <h2 className="sectionHeading">{t("home.progressTitle")}</h2>
                </div>
                <div className="progressControls">
                    <div
                        className="progressTabs"
                        role="tablist"
                        aria-label={t("home.progressTabsLabel")}
                    >
                        <button
                            type="button"
                            role="tab"
                            aria-selected={progressView === "muscleGroups"}
                            className={`progressTab${progressView === "muscleGroups" ? " progressTab--active" : ""}`}
                            onClick={() => setProgressView("muscleGroups")}
                        >
                            {t("home.progressByMuscleGroups")}
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={progressView === "exercisesByDate"}
                            className={`progressTab${progressView === "exercisesByDate" ? " progressTab--active" : ""}`}
                            onClick={() => setProgressView("exercisesByDate")}
                        >
                            {t("home.progressByDate")}
                        </button>
                    </div>
                    {progressView === "muscleGroups" && (
                        <div className="progressDateNav progressDateNav--desktop">
                            <button
                                type="button"
                                className="dayNavButton dayNavButton--prev dayNavButton--active"
                                onClick={handlePrevDay}
                                aria-label={t("home.prevDay")}
                                title={t("home.prevDay")}
                            >
                                <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden="true"
                                    focusable="false"
                                >
                                    <polyline points="15 18 9 12 15 6" />
                                </svg>
                            </button>
                            <button
                                type="button"
                                className={`todayNavButton${isTodayActive ? " todayNavButton--inactive" : " todayNavButton--active"}`}
                                onClick={handleGoToToday}
                                disabled={isTodayActive}
                                aria-disabled={isTodayActive}
                                aria-label={isTodayActive ? t("home.today") : t("home.goToToday")}
                                title={isTodayActive ? t("home.today") : t("home.goToToday")}
                            >
                                <span>{isTodayActive ? t("home.today") : t("home.goToToday")}</span>
                                {!isTodayActive && (
                                    <svg
                                        className="todayNavButtonArrowIcon"
                                        width="12"
                                        height="12"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        aria-hidden="true"
                                        focusable="false"
                                    >
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                        <polyline points="12 5 19 12 12 19" />
                                    </svg>
                                )}
                            </button>
                            <button
                                type="button"
                                className={`dayNavButton dayNavButton--next${progressDateIndex > 0 ? " dayNavButton--active" : " dayNavButton--inactive"}`}
                                onClick={handleNextDay}
                                disabled={progressDateIndex === 0}
                                aria-disabled={progressDateIndex === 0}
                                aria-label={t("home.nextDay")}
                                title={t("home.nextDay")}
                            >
                                <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden="true"
                                    focusable="false"
                                >
                                    <polyline points="9 18 15 12 9 6" />
                                </svg>
                            </button>
                        </div>
                    )}
                    {hasAnyLoggedExercises && progressView === "muscleGroups" && (
                        <button
                            type="button"
                            className={`indirectToggleButton${showIndirect ? " indirectToggleButton--active" : ""}`}
                            onClick={() => setShowIndirect((prev) => !prev)}
                            aria-pressed={showIndirect}
                            aria-label={t("home.toggleIndirectAria")}
                            title={t("home.toggleIndirectAria")}
                        >
                            <input
                                type="checkbox"
                                checked={showIndirect}
                                readOnly
                                aria-hidden="true"
                            />
                            <span>{t("home.toggleIndirect")}</span>
                        </button>
                    )}
                </div>
                <div className="tableContainer">
                    {progressView === "muscleGroups" ? (
                        <WorkoutProgressTable
                            dates={visibleDates}
                            exerciseLog={exerciseLog}
                            workoutNotes={workoutNotes}
                            onOpenNoteModal={handleOpenNoteModal}
                            showIndirect={showIndirect}
                            dateIndex={progressDateIndex}
                            onDateIndexChange={setProgressDateIndex}
                        />
                    ) : (
                        <ExerciseProgressTable
                            dates={visibleDates}
                            exerciseLog={exerciseLog}
                            workoutNotes={workoutNotes}
                            onOpenNoteModal={handleOpenNoteModal}
                        />
                    )}
                </div>
            </div>

            <WorkoutNoteModal
                isOpen={noteModal.isOpen}
                date={noteModal.date}
                initialNote={noteModal.initialNote}
                onSave={handleSaveNote}
                onClose={handleCloseNoteModal}
            />

            <ClearPlannedModal
                isOpen={isClearModalOpen}
                count={plannedExercisesCount}
                onConfirm={handleConfirmClearPlanned}
                onClose={handleCloseClearModal}
            />

            { /* Export/Import buttons removed as requested */ }
        </div>
    );
}

export default Home;

